import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { orderApi } from '../../services/api'
import Button from '../../components/common/Button'

/**
 * PendingPayments — chair's workspace for recording payments.
 *
 * Default view: "Unpaid" — every order with no payment_method recorded
 * (excluding cancelled). This covers:
 *   - Brand new pending_payment orders
 *   - Packed orders waiting on cash at pickup
 *   - Picked-up orders where cash was collected but not yet recorded
 *
 * Each card shows a 3-step progress indicator (Paid → Packed → Picked Up)
 * so the chair sees the full pipeline at a glance.
 *
 * Once payment is recorded, the order drops out of the default view.
 * Mark-as-Paid does NOT roll the status backward — if an order is
 * already packed or picked_up, only the payment fields are recorded.
 */
export default function PendingPayments() {
    const [orders, setOrders] = useState([])
    const [loading, setLoading] = useState(true)
    const [statusFilter, setStatusFilter] = useState('unpaid')

    // Modal state
    const [payingOrder, setPayingOrder] = useState(null)
    const [paymentMethod, setPaymentMethod] = useState('etransfer')
    const [paymentNotes, setPaymentNotes] = useState('')
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => { load() }, [statusFilter])

    async function load() {
        setLoading(true)
        try {
            const response = await orderApi.listAll()
            if (response.success) setOrders(response.orders)
            else toast.error(response.message || 'Could not load orders')
        } catch {
            toast.error('Network error')
        } finally {
            setLoading(false)
        }
    }

    // Filter client-side (the API fetches all; we slice here)
    const filtered = useMemo(() => {
        if (statusFilter === 'unpaid') {
            return orders.filter(o => !o.paymentMethod && o.status !== 'cancelled')
        }
        if (!statusFilter) return orders
        return orders.filter(o => o.status === statusFilter)
    }, [orders, statusFilter])

    // Summary stats — total $ across the filtered set
    const filteredTotal = useMemo(() => {
        return filtered.reduce((s, o) => s + Number(o.subtotal), 0)
    }, [filtered])

    function openMarkPaid(order) {
        setPayingOrder(order)
        setPaymentMethod('etransfer')
        setPaymentNotes('')
    }

    function closeModal() {
        setPayingOrder(null)
        setPaymentMethod('etransfer')
        setPaymentNotes('')
    }

    async function submitMarkPaid() {
        if (!payingOrder) return
        setSubmitting(true)
        try {
            const response = await orderApi.updateStatus(payingOrder.id, {
                status: 'paid',
                paymentMethod,
                paymentNotes: paymentNotes.trim() || null,
            })
            if (response.success) {
                toast.success(`Payment recorded for order #${payingOrder.id}`)
                closeModal()
                load()
            } else {
                toast.error(response.message || 'Could not record payment')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-2xl font-semibold text-ghasc-navy">Pending Payments</h2>
                <p className="text-sm text-gray-500 mt-1">
                    Orders where payment hasn't been recorded yet. Click <strong>Mark as Paid</strong> when
                    an e-transfer arrives or cash is collected at the meeting.
                </p>
            </div>

            {/* Filter + summary bar */}
            <div className="bg-white rounded-xl shadow-sm p-4 grid sm:grid-cols-3 gap-4 items-end">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Show</label>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                    >
                        <option value="unpaid">Unpaid (default)</option>
                        <option value="pending_payment">Pending Payment</option>
                        <option value="paid">Paid</option>
                        <option value="packed">Packed</option>
                        <option value="picked_up">Picked Up</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="">All</option>
                    </select>
                </div>
                <div>
                    <div className="text-xs uppercase tracking-wider text-gray-500">Count</div>
                    <div className="font-semibold text-lg text-ghasc-navy">{filtered.length}</div>
                </div>
                <div>
                    <div className="text-xs uppercase tracking-wider text-gray-500">
                        {statusFilter === 'unpaid' ? 'Amount Owed' : 'Total Value'}
                    </div>
                    <div className="font-semibold text-lg text-ghasc-navy">
                        ${filteredTotal.toFixed(2)}
                    </div>
                </div>
            </div>

            {/* List */}
            {loading ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">Loading...</div>
            ) : filtered.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
                    {statusFilter === 'unpaid'
                        ? 'All caught up — no unpaid orders. 🎉'
                        : 'No orders match that filter.'}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filtered.map(o => {
                        const submittedDate = new Date(o.createdAt)
                        const reference = buildOrderRef(o, submittedDate)
                        const isUnpaid = !o.paymentMethod && o.status !== 'cancelled'
                        return (
                            <div key={o.id} className="bg-white rounded-xl shadow-sm p-4 flex flex-col gap-3">
                                {/* Top row: order id + date */}
                                <div>
                                    <Link to={`/orders/${o.id}`} className="text-ghasc-navy font-semibold hover:underline">
                                        Order #{o.id}
                                    </Link>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                        {submittedDate.toLocaleDateString('en-US', {
                                            month: 'short', day: 'numeric', year: 'numeric',
                                        })}
                                    </div>
                                </div>

                                {/* Status progress: ✓ Paid → ✓ Packed → ✓ Picked Up */}
                                <StatusProgress order={o} />

                                {/* Customer + submitter */}
                                <div>
                                    <div className="text-sm font-medium text-gray-900">{o.customerName}</div>
                                    <div className="text-xs text-gray-500">Submitted by {o.submitterName}</div>
                                </div>

                                {/* E-transfer reference (chair needs this to match incoming payments) */}
                                <div className="bg-gray-50 rounded-lg p-3">
                                    <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                                        E-transfer reference
                                    </div>
                                    <div className="text-sm font-mono text-gray-700 break-words">
                                        {reference}
                                    </div>
                                </div>

                                {/* Total + Action */}
                                <div className="flex items-center justify-between gap-2 pt-1 border-t border-gray-100">
                                    <div className="text-lg font-semibold text-ghasc-navy">
                                        ${Number(o.subtotal).toFixed(2)}
                                    </div>
                                    {isUnpaid && (
                                        <Button onClick={() => openMarkPaid(o)}>
                                            Mark as Paid
                                        </Button>
                                    )}
                                </div>

                                {/* Payment details for already-paid orders */}
                                {o.paymentMethod && (
                                    <div className="text-xs text-gray-500 border-t border-gray-100 pt-2">
                                        Paid via <strong className="text-gray-700">{o.paymentMethod}</strong>
                                        {o.paymentNotes && <div className="mt-1 italic">"{o.paymentNotes}"</div>}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}

            {/* ----- Mark as Paid modal -------------------------------------- */}
            {payingOrder && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
                    onClick={closeModal}>
                    <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 space-y-4"
                        onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-ghasc-navy">
                            Mark order #{payingOrder.id} as paid
                        </h3>
                        <p className="text-sm text-gray-600">
                            <strong>{payingOrder.customerName}</strong> · ${Number(payingOrder.subtotal).toFixed(2)}
                        </p>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Payment method
                            </label>
                            <select
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                            >
                                <option value="etransfer">E-transfer</option>
                                <option value="cash">Cash</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Notes (optional)
                            </label>
                            <textarea
                                value={paymentNotes}
                                onChange={(e) => setPaymentNotes(e.target.value)}
                                rows={2}
                                placeholder="E-transfer reference number, who paid in cash, etc."
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                            />
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="secondary" onClick={closeModal} disabled={submitting}>
                                Cancel
                            </Button>
                            <Button onClick={submitMarkPaid} loading={submitting}>
                                Confirm Paid
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

// ============================================================================
//  StatusProgress — 3-step progress indicator
//  ✓ Paid    ✓ Packed    ○ Picked Up
//  Filled checkmark = done. Empty circle = not yet.
//  Cancelled orders show a different state.
// ============================================================================
function StatusProgress({ order }) {
    if (order.status === 'cancelled') {
        return (
            <div className="text-xs text-gray-600 font-medium">
                <span className="inline-block px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                    Cancelled
                </span>
            </div>
        )
    }

    // Paid step uses the actual payment_method field — not the status.
    // This way, an order marked picked_up without a recorded payment
    // correctly shows ○ Paid ✓ Packed ✓ Picked Up.
    const isPaid = !!order.paymentMethod
    const isPacked = order.status === 'packed' || order.status === 'picked_up'
    const isPickedUp = order.status === 'picked_up'

    return (
        <div className="flex items-center justify-between text-xs">
            <Step label="Paid" done={isPaid} />
            <Connector done={isPacked} />
            <Step label="Packed" done={isPacked} />
            <Connector done={isPickedUp} />
            <Step label="Picked Up" done={isPickedUp} />
        </div>
    )
}

function Step({ label, done }) {
    return (
        <div className="flex items-center gap-1">
            <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${done
                ? 'bg-green-500 text-white'
                : 'bg-gray-200 text-gray-400 border border-gray-300'
                }`}>
                {done ? '✓' : '○'}
            </span>
            <span className={done ? 'text-gray-900 font-medium' : 'text-gray-400'}>
                {label}
            </span>
        </div>
    )
}

function Connector({ done }) {
    return (
        <div className={`flex-1 h-0.5 mx-1 ${done ? 'bg-green-400' : 'bg-gray-200'}`} />
    )
}

/**
 * Reconstruct the e-transfer reference the customer was instructed to use.
 */
function buildOrderRef(order, createdAt) {
    const date = createdAt.toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
    })
    return `Order ${order.customerName} ${date}`
}